// ProposalMain.js

import React, {useEffect, useMemo, useRef, useState} from "react";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import qs from "qs";

import PdfViewerModal from "./modal/PdfViewerModal";
import ProposalVirtualList from "./component/ProposalVirtualList";

export default function ProposalMain() {
    const history = useHistory();
    const location = useLocation();

    // 검색 폼에서 사용하는 상태
    const [formData, setFormData] = useState({
        category: "",
        subCategory: "",
        year: "",
        name: "",
        keyword: "",
        tag: "",
    });

    // 실제 리스트 (API 결과)
    const [list, setList] = useState([]);

    // PDF 미리보기 모달 상태
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewPdf, setPreviewPdf] = useState(null);

    // “리스트 표시 여부” (입력 중일 때는 false, 검색 버튼 누르면 true)
    const [showList, setShowList] = useState(false);

    // 업로드 모달 열림 여부
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

    // URLSearchParams
    const query = new URLSearchParams(location.search);

    const [availableHeight, setAvailableHeight] = useState(800); // 기본값

    const topRef = useRef(null);

    useEffect(() => {
        function updateHeight() {
            const topHeight = topRef.current?.offsetHeight || 0;
            const windowHeight = window.innerHeight;
            setAvailableHeight(windowHeight - topHeight - 40); // 여유 여백 40
        }

        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, []);


    // ========== 목록 불러오기 ==========
    useEffect(() => {
        const fetchList = async () => {
            try {
                const tagsParam = query.getAll("tags");
                const res = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/proposal/list`,
                    {
                        params: {
                            category: query.get("category") || undefined,
                            subCategory: query.get("subCategory") || undefined,
                            year: query.get("year") || undefined,
                            name: query.get("name") || undefined,
                            keyword: query.get("keyword") || undefined,
                            tags: tagsParam.length > 0 ? tagsParam : undefined,
                        },
                        paramsSerializer: {
                            serialize: (params) =>
                                qs.stringify(params, { arrayFormat: "repeat" }),
                        },
                    }
                );
                setList(res.data);
            } catch (err) {
                console.error("목록 가져오기 실패:", err);
            }
        };
        // location.search가 바뀌면 fetchList
        fetchList();
    }, [location.search]);

    // ========== location.search → 폼값 세팅 ==========
    useEffect(() => {
        setFormData({
            category: query.get("category") || "",
            subCategory: query.get("subCategory") || "",
            year: query.get("year") || "",
            name: query.get("name") || "",
            keyword: query.get("keyword") || "",
            tag: query.getAll("tags").join(","),
        });
        // location.search 바뀔 때마다, 검색된 리스트가 있으므로 보여주기(true)
        setShowList(true);
    }, [location.search]);

    // ========== 폼 입력 시 ==========
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // 사용자가 입력을 시작했으므로, 리스트는 당분간 안 보여주기
        setShowList(false);
    };

    // ========== 검색 버튼 클릭 시 ==========
    const handleSearch = (e) => {
        e.preventDefault();
        const sp = new URLSearchParams();

        if (formData.category) sp.set("category", formData.category);
        if (formData.subCategory) sp.set("subCategory", formData.subCategory);
        if (formData.year) sp.set("year", formData.year);
        if (formData.name) sp.set("name", formData.name);
        if (formData.keyword) sp.set("keyword", formData.keyword);

        // 태그 쉼표 분리 → 파라미터
        if (formData.tag) {
            formData.tag.split(",").forEach((t) => {
                const trimmed = t.trim();
                if (trimmed) {
                    sp.append("tags", trimmed);
                }
            });
        }

        // 쿼리 파라미터 변경 → useEffect(fetchList) 다시 호출
        history.push({
            pathname: "/onandme/proposal",
            search: `?${sp.toString()}`,
        });

        // 이제 “검색 결과를 보여주도록” showList = true
        setShowList(true);
    };

    // ========== 초기화 버튼 클릭 시 ==========
    const handleReset = () => {
        setFormData({
            category: "",
            subCategory: "",
            year: "",
            name: "",
            keyword: "",
            tag: "",
        });
        // 쿼리 파라미터 제거
        history.push("/onandme/proposal");
        setShowList(false);
    };

    // ========== 미리보기 모달 ==========
    const handlePreview = (item, pageNumber) => {
        const matchedFirstPage = pageNumber || item.matchedPages?.[0]?.page || 1;
        setPreviewPdf({
            path: item.path,
            initialPage: matchedFirstPage,
            matchedPages: item.matchedPages || [],
            name: item.name,
        });
        setIsPreviewOpen(true);
    };

    const handleCloseModal = () => {
        setIsPreviewOpen(false);
        setPreviewPdf(null);
    };

    const handleDownload = () => {
        if (!previewPdf?.path) return;
        const a = document.createElement("a");
        a.href = previewPdf.path;
        a.download = previewPdf.path.split("/").pop();
        a.click();
    };

    // ========== 삭제 기능 ==========
    const handleDelete = async (id) => {
        if (!window.confirm("삭제하시겠습니까?")) return;
        try {
            await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/proposal/${id}`);
            alert("삭제되었습니다.");

            // 지금 파라미터 그대로 fetchList()를 다시 불러오기 위해 replace
            const currentQuery = new URLSearchParams(location.search).toString();
            history.replace(`/onandme/proposal?${currentQuery}`);
        } catch (error) {
            console.error("삭제 실패:", error);
            alert("삭제에 실패했습니다.");
        }
    };

    // ========== matchedPages → expandedList 생성 ==========
    const expandedList = useMemo(() => {
        return list.flatMap((item) => {
            if (!item.matchedPages || item.matchedPages.length === 0) {
                return [item];
            }
            return item.matchedPages.map((mp) => ({
                ...item,
                pageNumber: mp.page,
            }));
        });
    }, [list]);

    // ========== 업로드 모달 열기/닫기 + 이동 ==========
    const openUploadModal = () => {
        setIsUploadModalOpen(true);
    };

    const closeUploadModal = () => {
        setIsUploadModalOpen(false);
    };

    // 특정 업로드 항목 선택 시
    const handleUploadLinkClick = (url) => {
        // 모달 닫고
        setIsUploadModalOpen(false);
        // 해당 URL로 이동
        history.push(url);
    };

    // ========== 최종 렌더링 ==========
    return (
        <div className="pt-[80px] p-8">
            {/* 상단 안내 + 업로드 버튼 */}
            <div className="flex flex-col lg:flex-row justify-between items-center gap-12 mb-20">
                <div className="flex items-center gap-8">
                    <img src="/proposal/info.png" alt="info" className="w-12 h-12" />
                    <span className="text-3xl lg:text-3xl font-medium">
            제안서를 PDF 형식으로 업로드하세요.
          </span>
                </div>
                {/* 모달 열기 버튼으로 변경 */}
                <button
                    className="bg-blue-500 text-white text-2xl px-12 py-6 rounded-full flex items-center gap-6 hover:bg-blue-600"
                    onClick={openUploadModal}
                >
                    UPLOAD
                    <img src="/proposal/upload.png" alt="upload" className="w-5 h-5" />
                </button>
            </div>

            {/* 검색 폼 */}
            <form className="bg-white p-6 rounded-lg shadow mb-6" onSubmit={handleSearch}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {/* 카테고리 */}
                    <div>
                        <label className="block mb-1 font-semibold">카테고리</label>
                        <select
                            className="w-full border rounded p-2"
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                        >
                            <option value="">전체</option>
                            <option value="PROPOSAL">PROPOSAL</option>
                            <option value="PRESENTATION">PRESENTATION</option>
                        </select>
                    </div>

                    {/* 분류 */}
                    <div>
                        <label className="block mb-1 font-semibold">분류</label>
                        <select
                            className="w-full border rounded p-2"
                            name="subCategory"
                            value={formData.subCategory}
                            onChange={handleChange}
                        >
                            <option value="">전체</option>
                            <option value="MICE">MICE</option>
                            <option value="VIDEO_PLANNING">영상기획</option>
                        </select>
                    </div>

                    {/* 년도 */}
                    <div>
                        <label className="block mb-1 font-semibold">년도</label>
                        <select
                            className="w-full border rounded p-2"
                            name="year"
                            value={formData.year}
                            onChange={handleChange}
                        >
                            <option value="">전체</option>
                            {Array.from({ length: 5 }, (_, i) => {
                                const y = new Date().getFullYear() - i;
                                return (
                                    <option key={y} value={y}>
                                        {y}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    {/* 행사명 */}
                    <div>
                        <label className="block mb-1 font-semibold">행사 검색(포함 단어)</label>
                        <input
                            type="text"
                            className="w-full border rounded p-2"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    {/* 키워드 */}
                    <div>
                        <label className="block mb-1 font-semibold">
                            제안서 내 키워드 검색(포함 키워드 표시)
                        </label>
                        <input
                            type="text"
                            className="w-full border rounded p-2"
                            name="keyword"
                            value={formData.keyword}
                            onChange={handleChange}
                            placeholder="키워드를 입력하세요"
                        />
                    </div>

                    {/* 태그 */}
                    <div>
                        <label className="block mb-1 font-semibold">태그</label>
                        <input
                            type="text"
                            className="w-full border rounded p-2"
                            name="tag"
                            value={formData.tag}
                            onChange={handleChange}
                            placeholder="쉼표(,)로 구분 입력"
                        />
                    </div>
                </div>

                <div className="mt-6 text-right">
                    <button
                        type="submit"
                        className="px-6 py-3 text-lg font-bold bg-blue-500 text-white rounded-full hover:bg-blue-600 mr-2"
                    >
                        검색
                    </button>
                    <button
                        type="button"
                        onClick={handleReset}
                        className="px-6 py-3 text-lg font-bold bg-gray-300 text-gray-700 rounded-full hover:bg-gray-400"
                    >
                        초기화
                    </button>
                </div>
            </form>

            {/* 리스트 (가상 스크롤) */}
            {showList && (
                <div className="bg-white p-2 rounded-lg shadow mb-1">
                    <ProposalVirtualList
                        items={expandedList}
                        onPreview={handlePreview}
                        onDelete={handleDelete}
                        // containerHeight={800}
                        containerHeight={availableHeight}
                        cardWidth={280}
                        cardHeight={600}
                        gapX={15}
                        gapY={40}
                    />
                </div>
            )}

            {/* PDF 미리보기 모달 */}
            {isPreviewOpen && previewPdf && (
                <div className="flex-1 overflow-auto bg-gray-50 p-4">
                    <PdfViewerModal
                        isOpen={isPreviewOpen}
                        onClose={handleCloseModal}
                        path={previewPdf.path}
                        keyword={formData.keyword} // 검색 키워드 하이라이트 시 사용 가능
                        matchedPages={previewPdf.matchedPages || []}
                        initialPage={previewPdf.initialPage}
                    />
                </div>
            )}

            {/* 업로드 모달 */}
            {isUploadModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    {/* 반투명 배경 (클릭 시 모달 닫기) */}
                    <div
                        className="absolute inset-0 bg-gray-50 bg-opacity-30"
                        onClick={closeUploadModal}
                    />

                    {/* 모달 콘텐츠 박스 */}
                    <div className="relative bg-white rounded-lg shadow p-6 w-[300px]">
                        <h2 className="text-xl font-bold mb-4">업로드 종류</h2>
                        <ul className="space-y-2">
                            <li>
                                <button
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded"
                                    onClick={() => handleUploadLinkClick("/onandme/proposal/upload")}
                                >
                                    제안서 업로드
                                </button>
                            </li>
                            <li>
                                <button
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded"
                                    onClick={() => handleUploadLinkClick("/onandme/upload/2d")}
                                >
                                    2D(포스터) 업로드
                                </button>
                            </li>
                            <li>
                                <button
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded"
                                    onClick={() => handleUploadLinkClick("/onandme/upload/3d")}
                                >
                                    3D 업로드
                                </button>
                            </li>

                            <li>
                                <button
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded"
                                    onClick={() => handleUploadLinkClick("/onandme/upload/event-venue")}
                                >
                                    행사장 업로드
                                </button>
                            </li>
                        </ul>
                        {/* 닫기 버튼 */}
                        <div className="text-right mt-4">
                            <button
                                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                                onClick={closeUploadModal}
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}